<template>
  <section 
    id="about-section" 
    class="about-section"
    :aria-label="$t('about.sectionLabel')"
  >
    <div class="about-section__container">
      <!-- About Content -->
      <div class="about-section__content">
        <div class="about-section__text">
          <h2
            v-if="content.aboutTitle"
            class="about-section__title"
          >
            {{ content.aboutTitle }}
          </h2>
          <div
            v-if="content.aboutDescription"
            class="about-section__description"
            v-html="content.aboutDescription"
          />
          
          <!-- Features List -->
          <ul 
            class="about-section__features"
            v-if="content.aboutFeatures && content.aboutFeatures.length > 0"
          >
            <li 
              v-for="(feature, index) in content.aboutFeatures"
              :key="`feature-${index}`"
              class="about-section__feature"
            >
              <span class="about-section__feature-icon" aria-hidden="true">✓</span>
              <span class="about-section__feature-text">{{ feature }}</span>
            </li>
          </ul>
        </div>

        <!-- About Image -->
        <div
          v-if="content.aboutImage?.url"
          class="about-section__image">
          <img
            :src="content.aboutImage.url"
            :alt="content.aboutImage.alt"
            class="about-section__img"
            loading="lazy"
          />
        </div>
      </div>

      <!-- Metrics Section -->
      <div
        v-if="hasMetrics"
        class="about-section__metrics">
        <h3
          v-if="content.metricsTitle"
          class="about-section__metrics-title"
        >
          {{ content.metricsTitle }}
        </h3>
        
        <div class="about-section__metrics-grid">
          <div 
            v-for="(metric, index) in displayMetrics"
            :key="`metric-${index}`"
            :class="['about-section__metric', metric.type ? `about-section__metric--${metric.type}` : null]"
          >
            <div
              v-if="metric.image?.url"
              class="about-section__metric-image"
            >
              <img
                :src="metric.image.url"
                :alt="metric.image.alt || metric.label"
                loading="lazy"
              />
            </div>
            <div
              v-if="metric.icon"
              class="about-section__metric-icon"
              :aria-label="metric.iconLabel ?? undefined"
            >
              {{ metric.icon }}
            </div>
            <div class="about-section__metric-content">
              <div class="about-section__metric-number">
                <span 
                  :data-target="metric.value"
                  class="about-section__metric-value"
                >
                  {{ hasAnimated ? animatedValues[index] : metric.value }}
                </span>
                <span
                  v-if="metric.suffix"
                  class="about-section__metric-suffix"
                >
                  {{ metric.suffix }}
                </span>
              </div>
              <div class="about-section__metric-label">
                {{ metric.label }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'

interface AboutImage {
  url: string
  alt?: string
}

interface MetricImage {
  url: string
  alt?: string
}

interface Metric {
  type?: string
  icon?: string
  iconLabel?: string
  value: number
  label: string
  suffix?: string
  image?: MetricImage
}

type MetricInput = Partial<Omit<Metric, 'value' | 'label'>> & {
  value?: unknown
  label?: unknown
  image?: {
    url?: unknown
    alt?: unknown
  } | null
}

interface AboutContent {
  aboutTitle?: string
  aboutDescription?: string
  aboutFeatures?: string[]
  aboutImage?: AboutImage
  metricsTitle?: string
  metrics?: MetricInput[] | Record<string, MetricInput>
}

interface Props {
  content: AboutContent
}

const props = defineProps<Props>()

const animatedValues = ref<number[]>([])
const observer = ref<IntersectionObserver | null>(null)
const hasAnimated = ref(false)
const isMounted = ref(false)

const parseNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }

  return null
}

const sanitizeMetric = (metric: MetricInput | null | undefined): Metric | null => {
  if (!metric || typeof metric !== 'object') {
    return null
  }

  const value = parseNumber(metric.value)
  if (value === null) {
    return null
  }

  if (typeof metric.label !== 'string' || metric.label.trim().length === 0) {
    return null
  }

  const sanitized: Metric = {
    value,
    label: metric.label.trim()
  }

  if (typeof metric.type === 'string' && metric.type.trim().length > 0) {
    sanitized.type = metric.type.trim()
  }

  if (typeof metric.icon === 'string' && metric.icon.trim().length > 0) {
    sanitized.icon = metric.icon
  }

  if (typeof metric.iconLabel === 'string' && metric.iconLabel.trim().length > 0) {
    sanitized.iconLabel = metric.iconLabel.trim()
  }

  if (typeof metric.suffix === 'string' && metric.suffix.trim().length > 0) {
    sanitized.suffix = metric.suffix.trim()
  }

  if (metric.image && typeof metric.image === 'object') {
    const imageUrl = typeof metric.image.url === 'string' ? metric.image.url.trim() : ''
    if (imageUrl.length > 0) {
      const image: MetricImage = {
        url: imageUrl
      }

      if (typeof metric.image.alt === 'string' && metric.image.alt.trim().length > 0) {
        image.alt = metric.image.alt.trim()
      }

      sanitized.image = image
    }
  }

  return sanitized
}

const displayMetrics = computed<Metric[]>(() => {
  const metricsSource = props.content.metrics

  if (!metricsSource) {
    return []
  }

  const candidates: MetricInput[] = Array.isArray(metricsSource)
    ? metricsSource
    : Object.values(metricsSource)

  return candidates
    .map((metric) => sanitizeMetric(metric))
    .filter((metric): metric is Metric => metric !== null)
})

const hasMetrics = computed(() => displayMetrics.value.length > 0)

// Methods
function animateNumber(
  start: number,
  end: number,
  duration: number,
  index: number
): void {
  const startTime = performance.now()
  
  const animate = (currentTime: number) => {
    const elapsed = currentTime - startTime
    const progress = Math.min(elapsed / duration, 1)
    
    // Easing function (ease-out cubic)
    const easeOut = 1 - Math.pow(1 - progress, 3)
    const current = Math.floor(start + (end - start) * easeOut)
    
    animatedValues.value = animatedValues.value.map((existing, valueIndex) => {
      return valueIndex === index ? current : existing
    })
    
    if (progress < 1) {
      requestAnimationFrame(animate)
    } else {
      animatedValues.value = animatedValues.value.map((existing, valueIndex) => {
        return valueIndex === index ? end : existing
      })
    }
  }
  
  requestAnimationFrame(animate)
}

function handleIntersection(entries: IntersectionObserverEntry[]): void {
  if (!hasMetrics.value) {
    return
  }

  entries.forEach((entry) => {
    if (entry.isIntersecting && !hasAnimated.value) {
      hasAnimated.value = true
      
      displayMetrics.value.forEach((metric, index) => {
        const duration = 2000 + index * 300
        animateNumber(0, metric.value, duration, index)
      })
      
      if (observer.value) {
        observer.value.disconnect()
        observer.value = null
      }
    }
  })
}

function setupIntersectionObserver(): void {
  if (typeof window === 'undefined' || !hasMetrics.value) {
    return
  }

  if (observer.value) {
    observer.value.disconnect()
    observer.value = null
  }

  observer.value = new IntersectionObserver(handleIntersection, {
    threshold: 0.3,
    rootMargin: '0px 0px -100px 0px'
  })
  
  const metricsSection = document
    .getElementById('about-section')
    ?.querySelector('.about-section__metrics')

  if (metricsSection && observer.value) {
    observer.value.observe(metricsSection)
  }
}

watch(
  displayMetrics,
  (metrics) => {
    animatedValues.value = metrics.map(() => 0)
    hasAnimated.value = false

    if (!isMounted.value) {
      return
    }

    if (observer.value) {
      observer.value.disconnect()
      observer.value = null
    }

    if (metrics.length > 0) {
      setupIntersectionObserver()
    }
  },
  { immediate: true }
)

// Lifecycle
onMounted(() => {
  isMounted.value = true

  if (hasMetrics.value) {
    setupIntersectionObserver()
  }
})

onUnmounted(() => {
  if (observer.value) {
    observer.value.disconnect()
    observer.value = null
  }
})
</script>

<style lang="scss" scoped>
.about-section {
  padding: 80px 0;
  background: linear-gradient(135deg, #fdfbf7 0%, #f8f6f0 100%);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent 0%, #e5d5c8 50%, transparent 100%);
  }

  @media (max-width: 768px) {
    padding: 60px 0;
  }

  &__container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
  }

  &__content {
    align-items: center;
    margin-bottom: 80px;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: 40px;
      margin-bottom: 60px;
    }
  }



  &__title {
    font-size: 2.5rem;
    font-weight: 700;
    color: #2c1810;
    margin-bottom: 24px;
    line-height: 1.2;
    position: relative;
    padding-bottom: 12px;
    text-align: center;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 50%;
      transform: translateX(-50%);
      width: 60px;
      height: 4px;
      background: linear-gradient(90deg, #ff6b35, #ff8c42);
      border-radius: 2px;
    }

    @media (max-width: 768px) {
      font-size: 2rem;
      margin-bottom: 20px;
    }
  }

  &__description {
    font-size: 1.125rem;
    line-height: 1.7;
    color: #5d4e37;
    margin-bottom: 32px;

    :deep(p) {
      margin-bottom: 16px;
      
      &:last-child {
        margin-bottom: 0;
      }
    }

    @media (max-width: 768px) {
      font-size: 1rem;
      margin-bottom: 24px;
    }
  }

  &__features {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  &__feature {
    display: flex;
    align-items: center;
    margin-bottom: 12px;
    font-size: 1rem;
    color: #5d4e37;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__feature-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    background: #ff6b35;
    color: white;
    border-radius: 50%;
    font-size: 12px;
    font-weight: bold;
    margin-right: 12px;
    flex-shrink: 0;
  }

  &__feature-text {
    font-weight: 500;
  }

  &__image {
    position: relative;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 20px 40px rgba(44, 24, 16, 0.1);

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(45deg, rgba(255, 107, 53, 0.1) 0%, transparent 50%);
      z-index: 1;
    }
  }

  &__img {
    width: 100%;
    height: auto;
    display: block;
    transition: transform 0.3s ease;

    &:hover {
      transform: scale(1.02);
    }
  }

  // Metrics Section
  &__metrics {
    text-align: center;
  }

  &__metrics-title {
    font-size: 2rem;
    font-weight: 700;
    color: #2c1810;
    margin-bottom: 48px;

    @media (max-width: 768px) {
      font-size: 1.75rem;
      margin-bottom: 32px;
    }
  }

  &__metrics-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 40px;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: 32px;
    }

    @media (max-width: 480px) {
      gap: 24px;
    }
  }

  &__metric {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 32px 24px;
    background: white;
    border-radius: 16px;
    box-shadow: 0 8px 32px rgba(44, 24, 16, 0.08);
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 4px;
      background: #ff6b35;
      transform: scaleX(0);
      transition: transform 0.3s ease;
    }

    &:hover {
      transform: translateY(-8px);
      box-shadow: 0 16px 48px rgba(44, 24, 16, 0.12);

      &::before {
        transform: scaleX(1);
      }
    }

    @media (max-width: 768px) {
      padding: 24px 20px;
    }
  }

    &__metric-image {
      width: 64px;
      height: 64px;
      margin-bottom: 16px;
      border-radius: 12px;
      overflow: hidden;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      }
    }

  &__metric-icon {
    font-size: 3rem;
    margin-bottom: 16px;
    filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1));

    @media (max-width: 768px) {
      font-size: 2.5rem;
      margin-bottom: 12px;
    }
  }

  &__metric-content {
    text-align: center;
  }

  &__metric-number {
    font-size: 2.5rem;
    font-weight: 800;
    color: #2c1810;
    margin-bottom: 8px;
    line-height: 1;

    @media (max-width: 768px) {
      font-size: 2rem;
    }
  }

  &__metric-value {
    color: #ff6b35;
  }

  &__metric-suffix {
    color: #ff6b35;
  }

  &__metric-label {
    font-size: 1rem;
    color: #5d4e37;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    @media (max-width: 768px) {
      font-size: 0.875rem;
    }
  }
}

// Animation keyframes
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.about-section__content,
.about-section__metrics {
  animation: fadeInUp 0.8s ease-out;
}

.about-section__metric {
  animation: fadeInUp 0.8s ease-out;
  
  @for $i from 1 through 3 {
    &:nth-child(#{$i}) {
      animation-delay: #{($i - 1) * 0.2}s;
    }
  }
}
</style>
